import { useEffect, useRef } from 'react';

type AnyRecord = Record<string, any>;

function hasNonTimeFilterChanged<Filters extends AnyRecord, K extends keyof Filters>(
  prev: Filters,
  next: Filters,
  timeKey: K,
  excludeKeys?: string[]
): boolean {
  const keys = new Set<string>([...Object.keys(prev), ...Object.keys(next)]);
  keys.delete(String(timeKey));
  if (excludeKeys) {
    for (const ek of excludeKeys) keys.delete(ek);
  }
  for (const k of keys) {
    if ((prev as AnyRecord)[k] !== (next as AnyRecord)[k]) return true;
  }
  return false;
}

/**
 * Auto-clears a default time filter when the user changes any non-time filter.
 *
 * Conventions:
 * - undefined: "default mode" (UI can show default last-week range)
 * - null: user cleared time (keep empty)
 * - [start, end]: user selected time
 *
 * Behavior:
 * - If any non-time filter changes AND timeKey is still undefined AND user hasn't edited time yet,
 *   set timeKey to null (clear the time field).
 * - Once the user edits time (including clicking the clear/x icon), caller should set
 *   hasUserEditedTimeRef.current = true, and auto-clear will stop until reset/reload.
 */
export function useAutoClearDefaultTime<Filters extends AnyRecord, K extends keyof Filters>(params: {
  filters: Filters;
  setFilters: (next: Filters) => void;
  timeKey: K;
  hasUserEditedTimeRef: React.MutableRefObject<boolean>;
  // Some pages programmatically set default non-time filters on mount (e.g. hubId/type),
  // which would otherwise clear the default time immediately.
  // Enable this to ignore the first eligible non-time change.
  skipFirstNonTimeChange?: boolean;
  // Keys to exclude from non-time change detection (e.g. warehouse context fields
  // that change without user interaction and should not clear the default time).
  excludeKeys?: string[];
}) {
  const { filters, setFilters, timeKey, hasUserEditedTimeRef, skipFirstNonTimeChange, excludeKeys } = params;
  const prevFiltersRef = useRef<Filters>(filters);
  const skippedOnceRef = useRef(false);

  useEffect(() => {
    const prev = prevFiltersRef.current;
    const next = filters;

    // Only react to changes after initial mount.
    if (prev === next) return;

    const nonTimeChanged = hasNonTimeFilterChanged(prev, next, timeKey, excludeKeys);
    const timeValue = next[timeKey];
    const prevTimeValue = prev[timeKey];

    // Reset/unlock when time goes back to "default mode" (usually via Reset button)
    if (timeValue === undefined && prevTimeValue !== undefined) {
      hasUserEditedTimeRef.current = false;
    }

    if (nonTimeChanged && timeValue === undefined && !hasUserEditedTimeRef.current) {
      if (skipFirstNonTimeChange && !skippedOnceRef.current) {
        skippedOnceRef.current = true;
        prevFiltersRef.current = next;
        return;
      }
      const cleared = { ...next, [timeKey]: null } as Filters;
      prevFiltersRef.current = cleared;
      setFilters(cleared);
      return;
    }

    prevFiltersRef.current = next;
  }, [filters, setFilters, timeKey, hasUserEditedTimeRef]);
}

